import {
  Box,
  IconButton,
  Paper,
} from "@mui/material";
import { Refresh as RefreshIcon } from "@mui/icons-material";
import RowActionButtons from "../table/RowActionButtons";
import {
  DataGrid,
  type GridColDef,
  type GridRenderCellParams,
} from "@mui/x-data-grid";
import React, {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react";
import { getData } from "../../api/api";
import {
  buildReadQueryString,
  extractTableFieldsFromColumns,
  fetchReadRecordById,
  isPaginatedReadUrl,
  toFieldsParam,
} from "../../lib/api/readQuery";
import { getCurrentUserRoles, isViewer } from "../../utils/authorization";
import { notifySuccessShort } from "../../utils/notification";
import { rdtLikeToDataGridColumns, type RdtLikeColumn } from "../../lib/ui/rdtLikeToDataGridColumns";
import FilterPanel from "../FilterPanel";
import FilterSearchField from "../FilterPanel/FilterSearchField";
import { useTranslation } from "react-i18next";
import { fillDataGridSx, fillDataTableGridWrapSx, fillDataTableRootSx } from "../../lib/ui/pageLayout";

const DataTableWithPagination = React.forwardRef(
  (
    {
      cols,
      handleEdit,
      handleDelete,
      handleDownload,
      fetchUrl,
      handleView,
      handleDraw,
      searchKey,
      searchFields,
      searchPlaceholder,
      extraFilters,
      extraFilterCount = 0,
      onClearExtraFilters,
      expandableRows: _expandableRows,
      expandableRowsComponent: _expandableRowsComponent,
      additionalParameters,
      handleSelect,
      listFields,
      extraListFields,
      fetchRecordById,
      fetchReadRecordOnEdit = true,
      headerActions,
    }: any,
    ref,
  ) => {
    const { t } = useTranslation();
    const [data, setData] = useState<any>({ content: [], page: {} });
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [itemsPerPage, setItemsPerPage] = useState(50);
    const [committedSearch, setCommittedSearch] = useState("");
    const [searchNonce, setSearchNonce] = useState(0);
    const [viewerRole, setViewerRole] = useState(false);
    const [editLoadingId, setEditLoadingId] = useState<string | null>(null);
    const refreshButtonRef = useRef<HTMLButtonElement>(null);
    const fetchDataRef = useRef<() => Promise<void>>(async () => {});

    const searchFieldsKey = Array.isArray(searchFields) ? searchFields.join("|") : "";

    const listFieldsParam = useMemo(() => {
      if (listFields) {
        return toFieldsParam(listFields);
      }

      if (Array.isArray(cols) && cols.length > 0) {
        return toFieldsParam(extractTableFieldsFromColumns(cols as RdtLikeColumn[], extraListFields));
      }

      return undefined;
    }, [cols, extraListFields, listFields]);

    const supportsReadProjection = isPaginatedReadUrl(fetchUrl);

    const fetchData = useCallback(async () => {
      void searchNonce;
      setLoading(true);
      const raw = committedSearch;
      const encoded = encodeURIComponent(raw);
      const defaultSearchFields = searchKey
        ? [searchKey, "description"]
        : ["description"];
      const rsqlFields = Array.isArray(searchFields) && searchFields.length > 0
        ? searchFields
        : defaultSearchFields;
      const searchExpression = rsqlFields.map((field) => `${field}=ilike=${encoded}`).join(",");

      let searchParam = "";
      if (additionalParameters && raw) {
        searchParam = `${additionalParameters};(${searchExpression})`;
      } else if (additionalParameters) {
        searchParam = additionalParameters;
      } else if (raw) {
        searchParam = searchExpression;
      }

      const query = buildReadQueryString({
        page: currentPage - 1,
        size: itemsPerPage,
        sort: "createdDate,desc",
        search: searchParam || undefined,
        fields: supportsReadProjection ? listFieldsParam : undefined,
      });

      const result = await getData(`${fetchUrl}?${query}`);
      setData(result);
      setLoading(false);
    }, [
      currentPage,
      fetchUrl,
      itemsPerPage,
      searchKey,
      searchFieldsKey,
      committedSearch,
      additionalParameters,
      searchNonce,
      listFieldsParam,
      supportsReadProjection,
    ]);

    fetchDataRef.current = fetchData;

    useImperativeHandle(ref, () => ({
      refresh() {
        void fetchDataRef.current?.();
      },
    }));

    useEffect(() => {
      getCurrentUserRoles().then((roles) => setViewerRole(isViewer(roles)));
    }, []);

    useEffect(() => {
      fetchData();
    }, [currentPage, itemsPerPage, fetchData]);

    const currentItems = (data as any).content ?? [];
    const totalRows = (data as any)?.page?.totalElements ?? 0;

    const handleCopy = (row: any) => {
      navigator.clipboard.writeText(String(row.id ?? ""));
      notifySuccessShort(t("common.rowCopied"), t("common.copied"));
    };

    const loadRecordForEdit = useCallback(
      async (row: Record<string, unknown>) => {
        const id = row?.id != null ? String(row.id) : "";
        if (!fetchReadRecordOnEdit || !id) {
          return row;
        }

        if (fetchRecordById) {
          return fetchRecordById(id);
        }

        if (supportsReadProjection && fetchUrl) {
          return fetchReadRecordById(fetchUrl, id);
        }

        return row;
      },
      [fetchReadRecordOnEdit, fetchRecordById, fetchUrl, supportsReadProjection],
    );

    const handleEditClick = useCallback(
      async (row: Record<string, unknown>) => {
        if (!handleEdit) {
          return;
        }

        const id = row?.id != null ? String(row.id) : "";
        try {
          if (id) {
            setEditLoadingId(id);
          }
          const record = await loadRecordForEdit(row);
          await handleEdit(record);
        } catch (error) {
          console.error("Failed to load record for edit:", error);
          await handleEdit(row);
        } finally {
          setEditLoadingId(null);
        }
      },
      [handleEdit, loadRecordForEdit],
    );

    const handleViewClick = useCallback(
      async (row: Record<string, unknown>) => {
        if (!handleView) {
          return;
        }

        const id = row?.id != null ? String(row.id) : "";
        try {
          if (id) {
            setEditLoadingId(id);
          }
          const record = await loadRecordForEdit(row);
          await handleView(record);
        } catch (error) {
          console.error("Failed to load record for view:", error);
          await handleView(row);
        } finally {
          setEditLoadingId(null);
        }
      },
      [handleView, loadRecordForEdit],
    );

    const renderActions = useCallback(
      (params: GridRenderCellParams) => {
        const row = params.row;
        const rowId = row?.id != null ? String(row.id) : "";
        const isEditLoading = editLoadingId != null && editLoadingId === rowId;

        return (
          <RowActionButtons
            loading={isEditLoading}
            onView={handleView ? () => void handleViewClick(row) : undefined}
            onEdit={handleEdit ? () => void handleEditClick(row) : undefined}
            onDelete={handleDelete && !viewerRole ? () => void handleDelete(row) : undefined}
            onDraw={handleDraw ? () => void handleDraw(row) : undefined}
            onDownload={handleDownload ? () => void handleDownload(row) : undefined}
            onCopy={() => handleCopy(row)}
            copyValue={String(row.id ?? "")}
          />
        );
      },
      [handleEdit, handleDelete, handleView, handleDraw, handleDownload, handleEditClick, handleViewClick, editLoadingId, viewerRole],
    );

    const columns: GridColDef[] = useMemo(() => {
      const actionCol: GridColDef = {
        field: "__actions",
        headerName: t("common.actions"),
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        minWidth: 120,
        flex: 1,
        renderCell: renderActions,
      };
      const mapped = rdtLikeToDataGridColumns((cols || []) as RdtLikeColumn[], "d");
      return [actionCol, ...mapped];
    }, [cols, renderActions, t]);

    const getRowId = useCallback((row: Record<string, unknown>) => {
      const id = row.id ?? row.uuid;
      if (id != null && String(id) !== "") return String(id);
      return `row_${JSON.stringify(row).slice(0, 48)}`;
    }, []);

    const handleSearchApply = useCallback((next: string) => {
      setCommittedSearch(next);
      setCurrentPage(1);
      setSearchNonce((n) => n + 1);
    }, []);

    const handleClearFilters = useCallback(() => {
      handleSearchApply("");
      onClearExtraFilters?.();
    }, [handleSearchApply, onClearExtraFilters]);

    const activeFilterCount = extraFilterCount + (committedSearch.trim() ? 1 : 0);

    return (
      <Paper variant="outlined" sx={fillDataTableRootSx}>
        <Box sx={{ flexShrink: 0, width: "100%" }}>
          <FilterPanel
            embedded
            defaultOpen
            activeCount={activeFilterCount}
            onClear={activeFilterCount > 0 ? handleClearFilters : undefined}
            actions={
              <>
                <IconButton
                  ref={refreshButtonRef}
                  size="small"
                  onClick={() => void fetchData()}
                  color="primary"
                  aria-label={t("common.refreshTable")}
                  sx={{ width: 30, height: 30 }}
                >
                  <RefreshIcon fontSize="small" />
                </IconButton>
                {headerActions}
              </>
            }
          >
            {extraFilters}
            <FilterSearchField
              value={committedSearch}
              onApply={handleSearchApply}
              placeholder={searchPlaceholder ?? searchKey}
              disabled={loading}
            />
          </FilterPanel>
        </Box>

        <Box sx={fillDataTableGridWrapSx}>
          <DataGrid
            rows={Array.isArray(currentItems) ? currentItems : []}
            columns={columns}
            getRowId={getRowId}
            loading={loading}
            paginationMode="server"
            rowCount={totalRows}
            paginationModel={{
              page: Math.max(0, currentPage - 1),
              pageSize: itemsPerPage,
            }}
            onPaginationModelChange={(model) => {
              setCurrentPage(model.page + 1);
              setItemsPerPage(model.pageSize);
            }}
            pageSizeOptions={[50, 100, 200, 500]}
            sortingOrder={["desc", "asc"]}
            disableRowSelectionOnClick
            onRowDoubleClick={
              handleSelect
                ? (params) => handleSelect(params.row)
                : undefined
            }
            localeText={{ noRowsLabel: t("common.noRecords") }}
            sx={{
              ...fillDataGridSx,
              "& .MuiDataGrid-cell": { alignItems: "flex-start", py: 1 },
            }}
          />
        </Box>
      </Paper>
    );
  },
);

export default DataTableWithPagination;
